import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import TableData from "components/ui/TableData";
import BasicButton from "components/forms/BasicButton";
import SelectInput from "components/forms/SelectInput";
import { PlusIcon } from "components/svg";

import {
  getProposalsTable,
  listFollowUps,
  acknowledgeFollowUp,
  getProposalReporting,
  deleteProposal,
  type ProposalReporting,
} from "api/Proposals";

/**
 * Proposals directory.
 *
 * Built on the shared Header/Page/TableData shell every other directory in
 * the app uses, so paging, sorting, search and the filter drawer behave the
 * same way here as they do in Locations or Invoices. This module had its own
 * width, its own table and no page header, which made it read as a bolted-on
 * tool rather than part of the app.
 *
 * It is still a WORK QUEUE rather than a directory: the Next step column is
 * the reason to open it, and prospects who asked to be contacted sit above
 * the table because they are the most perishable thing on the screen.
 */

const columnHelper = createColumnHelper();

const createColumn = (
  id: string,
  title: string,
  accessor: (row: any) => any,
  cell?: (info: any) => any,
  options = {},
) =>
  columnHelper.accessor(accessor, {
    id,
    cell: cell || ((info) => info.getValue()),
    header: () => <span>{title}</span>,
    ...options,
  });

const STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  draft: { bg: "#EEF2F7", fg: "#5D5E62", label: "Draft" },
  sent: { bg: "#E8F1FB", fg: "#2F6FB5", label: "Sent" },
  // Frozen with a live link, not emailed from the app yet.
  link: { bg: "#F1EFE8", fg: "#5f5e5a", label: "Link ready" },
  viewed: { bg: "#E4F2EA", fg: "#1D7A57", label: "Opened" },
  won: { bg: "#667E4E", fg: "#FFFFFF", label: "Won" },
  lost: { bg: "#FBEAEA", fg: "#A33B3A", label: "Lost" },
};

const TYPE_LABEL: Record<string, string> = {
  multi_location_brand: "Cold multi-location brand",
  existing_client_portfolio: "Existing client",
  single_location: "Single location",
  completed_site: "Completed site",
};

const Pill: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({
  bg,
  fg,
  children,
}) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      background: bg,
      color: fg,
      borderRadius: 3,
      fontSize: 10,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

const ProposalsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [followUps, setFollowUps] = useState<any[]>([]);
  const [reporting, setReporting] = useState<ProposalReporting | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Seeded from the URL so the dashboard work queue can link straight to a
  // filtered list. A queue row that names a next step and then lands you on
  // an unfiltered page has spent the operator's trust to say nothing.
  const statusFromUrl = searchParams.get("status") ?? "";

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Proposals";
  }, []);

  const loadFollowUps = () => {
    listFollowUps().then((res: any) => {
      if (!res?.error) setFollowUps(res.items ?? []);
    });
  };

  useEffect(loadFollowUps, []);

  useEffect(() => {
    getProposalReporting(12).then((res: any) => {
      if (!res?.error) setReporting(res);
    });
  }, []);

  const ack = async (id: string) => {
    await acknowledgeFollowUp(id);
    loadFollowUps();
    setReloadKey((k) => k + 1);
  };

  const open = (row: any) =>
    navigate(row.status === "draft" ? `/proposals/${row.id}/edit` : `/proposals/${row.id}`);

  /**
   * Delete a DRAFT from the list (2026-10-01). A draft row opens the
   * wizard, not the detail page, so the detail page's Delete was
   * unreachable for drafts in practice. Sent proposals stay delete-from-
   * detail only: killing a live link deserves the full page in front of you.
   */
  const removeDraft = async (row: any) => {
    const ok = window.confirm(`Delete the draft "${row.name || row.prospectName}"? This cannot be undone from the app.`);
    if (!ok) return;
    const res: any = await deleteProposal(row.id);
    if (res?.error) {
      window.alert(res.error);
      return;
    }
    setReloadKey((k) => k + 1);
  };

  const tableFilters = useMemo(
    () => [
      {
        // key must match props.name so TableData's filter serializer finds
        // this config when the form submits, and the value lands at
        // query.filters.status server-side.
        key: "status",
        component: SelectInput,
        props: {
          name: "status",
          label: "Status",
          clearable: true,
          options: [
            { value: "draft", label: "Drafts" },
            { value: "sent", label: "Sent" },
            { value: "viewed", label: "Opened" },
            { value: "won", label: "Won" },
            { value: "lost", label: "Lost" },
          ],
          $customStyles: { minWidth: "200px" },
        },
      },
    ],
    [],
  );

  return (
    <>
      <Header />
      <Page
        title="Proposals"
        subtitle="Pricing proposals for prospects and existing clients, and what each one needs next."
        titleAction={
          <>
            <BasicButton outline scheme="secondary" keepText onClick={() => navigate("/proposals/feedback")}>
              Feedback
            </BasicButton>
            <BasicButton onClick={() => navigate("/proposals/new")} $iconHeight="10px">
              <PlusIcon fill="#fff" />
              New proposal
            </BasicButton>
          </>
        }
      >
        {reporting && reporting.sent > 0 && (
          <Flex
            sx={{
              gap: 0,
              mb: 20,
              border: "1px solid #E3E8EF",
              borderRadius: "6px",
              bg: "#fff",
              flexWrap: "wrap",
            }}
          >
            {/*
              Four numbers, chosen because they answer whether the tool works
              rather than how much it has been used. Win rate is over DECIDED
              proposals only — dividing wins by everything ever sent counts
              proposals still in flight as losses.
            */}
            <Stat label="Sent (12 mo)" value={reporting.sent} note={`${reporting.inFlight} still open`} />
            <Stat
              label="Opened"
              value={reporting.openRate != null ? `${reporting.openRate}%` : "\u2014"}
              note={
                reporting.avgHoursToOpen != null
                  ? `usually within ${Math.round(reporting.avgHoursToOpen)}h`
                  : "no opens yet"
              }
            />
            <Stat
              label="Won"
              value={reporting.winRate != null ? `${reporting.winRate}%` : "\u2014"}
              note={
                reporting.decided > 0
                  ? `of ${reporting.decided} decided`
                  : "nothing decided yet"
              }
            />
            <Stat
              label="Replies"
              value={reporting.replies}
              note={reporting.handsUp > 0 ? `${reporting.handsUp} asked for contact` : "none asked"}
            />
          </Flex>
        )}

        {followUps.length > 0 && (
          <Box
            sx={{
              mb: 20,
              p: "14px 18px",
              bg: "#E4F2EA",
              border: "1px solid #A8D5BF",
              borderRadius: "6px",
            }}
          >
            <Box sx={{ fontSize: 13, fontWeight: 600, color: "#1D7A57", mb: 8 }}>
              {followUps.length} {followUps.length === 1 ? "prospect has" : "prospects have"} asked
              us to follow up
            </Box>
            {followUps.map((f) => (
              <Flex
                key={f.id}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  py: "6px",
                  borderTop: "1px solid rgba(29,122,87,.15)",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ fontSize: 13 }}>
                  <strong>{f.prospectName}</strong>
                  <Box sx={{ fontSize: 11, color: "#5D5E62" }}>
                    {f.sentTo} &middot;{" "}
                    {f.responses?.pricing === "high"
                      ? "said pricing is too high"
                      : f.responses?.pricing === "aligned"
                      ? "said pricing aligns"
                      : "unsure on pricing"}{" "}
                    &middot; {moment(f.submittedAt).fromNow()}
                  </Box>
                </Box>
                <BasicButton
                  outline
                  scheme="secondary"
                  onClick={() => ack(f.id)}
                  keepText
                >
                  Mark handled
                </BasicButton>
              </Flex>
            ))}
          </Box>
        )}

        <TableData
          key={`${reloadKey}-${statusFromUrl}`}
          // One row per proposal lineage: versions sent to other contacts at
          // the same organization sit inside the row, not beside it.
          api={(args: any) => getProposalsTable({ ...args, query: { ...(args?.query ?? {}), grouped: "true" } })}
          stateKey="proposals"
          defaultSortBy="updatedAt"
          defaultSortDir="desc"
          defaultFilters={statusFromUrl ? { status: statusFromUrl } : {}}
          onFiltersChange={(f: any) => {
            // Keep the URL honest so a filtered list is shareable and the
            // back button does what it looks like it does.
            if (f?.status) setSearchParams({ status: f.status });
            else setSearchParams({});
          }}
          filters={tableFilters}
          nothingFoundText="No proposals yet. Start one and it will appear here with whatever needs doing next."
          title="All proposals"
          columns={[
            createColumn(
              "prospectName",
              "Prospect",
              (row: any) => row?.prospectName,
              (info: any) => {
                const row = info.row.original;
                return (
                  <Box sx={{ cursor: "pointer" }} onClick={() => open(row)}>
                    <Box sx={{ color: "#000", fontWeight: 500 }}>
                      {info.getValue()}
                      {row.version > 1 && (
                        <span style={{ color: "#818181", fontWeight: 400 }}> v{row.version}</span>
                      )}
                    </Box>
                    <Box sx={{ fontSize: 11, color: "#818181" }}>
                      {row.clientStage === "lead" && row.clientId ? (
                        <span
                          style={{ color: "#2F6FB5" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leads/${row.clientId}`);
                          }}
                        >
                          Lead: {row.clientName}
                        </span>
                      ) : (
                        row.clientName || TYPE_LABEL[row.customerType]
                      )}
                    </Box>
                    {row.versionCount > 1 && Array.isArray(row.versions) && (
                      <Box sx={{ fontSize: 11, color: "#5D5E62", mt: "2px" }}>
                        {`${row.versionCount} versions \u00b7 `}
                        {row.versions.map((v: any, idx: number) => (
                          <span key={v.id}>
                            {idx > 0 ? ", " : ""}
                            <span
                              style={{ color: "#2F6FB5", cursor: "pointer" }}
                              title={`v${v.version} \u00b7 ${STATUS_STYLE[v.displayStatus ?? v.status]?.label ?? v.status}${v.viewCount ? ` \u00b7 ${v.viewCount} opens` : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                open(v);
                              }}
                            >
                              v{v.version} {v.contactName || v.sentTo || "draft"}
                            </span>
                          </span>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              },
            ),
            createColumn(
              "status",
              "Status",
              (row: any) => row?.displayStatus ?? row?.status,
              (info: any) => {
                const s = STATUS_STYLE[info.getValue()] ?? STATUS_STYLE.draft;
                return (
                  <Pill bg={s.bg} fg={s.fg}>
                    {s.label}
                  </Pill>
                );
              },
            ),
            createColumn(
              "nextStep",
              "Next step",
              (row: any) => row?.nextStep,
              (info: any) => {
                const row = info.row.original;
                if (!info.getValue()) return <span style={{ color: "#B0B4BA" }}>—</span>;
                // Only two states earn colour: someone waiting on us, and a
                // proposal nobody has opened. Colouring every row would make
                // the column decorative.
                if (row.followUpRequested > 0) {
                  return (
                    <Pill bg="#E4F2EA" fg="#1D7A57">
                      {info.getValue()}
                    </Pill>
                  );
                }
                if (row.viewCount === 0 && (row.daysSinceSent ?? 0) >= 7) {
                  return (
                    <Pill bg="#FFF4E5" fg="#8A5A16">
                      {info.getValue()}
                    </Pill>
                  );
                }
                return <span style={{ fontSize: 13 }}>{info.getValue()}</span>;
              },
              { enableSorting: false },
            ),
            createColumn(
              "viewCount",
              "Opens",
              (row: any) => row?.viewCount,
              (info: any) => {
                const row = info.row.original;
                const n = row.versionCount > 1 ? row.totalViews : info.getValue();
                return n ? (
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{n}</span>
                ) : (
                  <span style={{ color: "#B0B4BA" }}>—</span>
                );
              },
            ),
            createColumn(
              "sentAt",
              "Sent",
              (row: any) => row?.sentAt,
              (info: any) =>
                info.getValue() ? (
                  moment(info.getValue()).format("MMM D, YYYY")
                ) : (
                  <span style={{ color: "#B0B4BA" }}>—</span>
                ),
            ),
            createColumn(
              "actions",
              "",
              (row: any) => row?.id,
              (info: any) => {
                const row = info.row.original;
                if (row.status !== "draft") return null;
                return (
                  <span
                    role="button"
                    tabIndex={0}
                    style={{ color: "#A33B3A", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDraft(row);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") removeDraft(row);
                    }}
                  >
                    Delete
                  </span>
                );
              },
              { enableSorting: false },
            ),
          ]}
        />
      </Page>
    </>
  );
};

const Stat: React.FC<{ label: string; value: any; note?: string }> = ({ label, value, note }) => (
  <Box
    sx={{
      flex: "1 1 160px",
      p: "14px 18px",
      borderRight: "1px solid #E3E8EF",
      "&:last-of-type": { borderRight: "none" },
    }}
  >
    <Box
      sx={{
        fontSize: 10,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        color: "#818181",
        fontWeight: 600,
      }}
    >
      {label}
    </Box>
    <Box sx={{ fontSize: 22, fontWeight: 700, color: "#2b3340", lineHeight: 1.2 }}>{value}</Box>
    {note && <Box sx={{ fontSize: 11, color: "#818181" }}>{note}</Box>}
  </Box>
);

export default ProposalsList;
